import { ulid } from 'ulidx'
import { nanoid } from 'nanoid'
import { createId as cuid2 } from '@paralleldrive/cuid2'
import { randomUUID } from 'node:crypto'

/**
 * Supported ID generation types.
 * Must match IdType from @gzl10/nexus-sdk
 */
export type IdType = 'ulid' | 'uuid' | 'nanoid' | 'cuid2' | 'auto' | 'custom' | 'pattern'

/**
 * Generates a unique ULID identifier.
 *
 * ULID properties:
 * - Time-sortable: lexicographically sortable by creation time
 * - Secure: 80 bits of cryptographically random entropy
 * - URL-safe: uppercase letters and numbers only (Crockford Base32)
 * - 26 characters fixed length
 *
 * @returns A unique ULID string (e.g., "01ARZ3NDEKTSV4RRFFQ69G5FAV")
 */
export function generateId(): string {
  return ulid()
}

/**
 * Generates a unique identifier based on the specified type.
 *
 * @param type - ID generation strategy
 * @returns Generated ID string, or undefined for 'auto'/'custom' types
 *
 * @example
 * ```typescript
 * generateIdByType('ulid')   // "01ARZ3NDEKTSV4RRFFQ69G5FAV"
 * generateIdByType('uuid')   // "550e8400-e29b-41d4-a716-446655440000"
 * generateIdByType('nanoid') // "V1StGXR8_Z5jdHi6B-myT"
 * generateIdByType('cuid2')  // "clh3am1u70000qj0f9fgk2e1x"
 * generateIdByType('auto')   // undefined (DB handles it)
 * generateIdByType('custom') // undefined (user provides it)
 * ```
 */
export function generateIdByType(type: IdType = 'ulid'): string | undefined {
  switch (type) {
    case 'ulid':
      return ulid()
    case 'uuid':
      return randomUUID()
    case 'nanoid':
      return nanoid()
    case 'cuid2':
      return cuid2()
    case 'auto':
      // Auto-increment: DB handles ID generation
      return undefined
    case 'custom':
      // Custom: user must provide the ID
      return undefined
    case 'pattern':
      // Pattern: handled by generatePatternId() in sequence.ts
      return undefined
    default:
      // Fallback to ULID for unknown types
      return ulid()
  }
}
